/**
 * Firebase Authentication Loader
 * Initializes Firebase and handles auth state for Moonfall
 * Cross-platform compatible (Web + Unity)
 */

let Firebase = {};

// Create UI namespace if it doesn't exist

// Firebase module
Firebase = (function () {
    let firebaseApp = null;
    let firebaseAuth = null;
    let currentUser = null;
    let onAuthChangeCallback = null;
    const authChangeSubscribers = new Set();
    // `onAuthStateChanged` fires on sign-in and sign-out only. A Firebase ID
    // token lives one hour and is refreshed silently, and only
    // `onIdTokenChanged` reports that — which is why the Convex client used to
    // sit on an expired credential for the rest of every long session.
    const idTokenSubscribers = new Set();

    /**
     * Initialize Firebase
     */
    function init(callback) {
        onAuthChangeCallback = callback;

        if (!window.firebase) {
            console.error('[Firebase] Firebase SDK not loaded');
            return false;
        }

        if (!window.FIREBASE_CONFIG) {
            console.error('[Firebase] Firebase config not found');
            return false;
        }

        // Initialize Firebase
        firebaseApp = firebase.initializeApp(window.FIREBASE_CONFIG);
        firebaseAuth = firebase.auth();

        console.log('[Firebase] Firebase initialized');

        // Listen for auth state changes
        firebaseAuth.onAuthStateChanged((user) => {
            currentUser = user;
            if (user) {
                console.log('[Firebase] User signed in');
            } else {
                console.log('[Firebase] User signed out');
            }

            authChangeSubscribers.forEach((subscriber) => {
                try {
                    subscriber(user);
                } catch (error) {
                    console.error('[Firebase] Auth subscriber failed:', error);
                }
            });
            if (onAuthChangeCallback) {
                onAuthChangeCallback(user);
            }
        });

        // Every fresh credential Firebase mints, republished to whoever holds a
        // copy of the old one. Guarded because a vendored SDK swap that lacks
        // the listener must degrade to the old behaviour, never take the whole
        // boot down with it and leave a signed-in player on the sign-in screen.
        if (typeof firebaseAuth.onIdTokenChanged === 'function') {
            firebaseAuth.onIdTokenChanged(publishIdToken);
        }

        return true;
    }

    /**
     * Check if user is signed in
     */
    function isSignedIn() {
        return currentUser !== null;
    }

    /**
     * Get current user
     */
    function getUser() {
        return currentUser;
    }

    function subscribe(callback) {
        if (typeof callback !== 'function') return () => {};
        authChangeSubscribers.add(callback);
        return () => authChangeSubscribers.delete(callback);
    }

    async function publishIdToken(user) {
        currentUser = user;
        let token = null;
        if (user) {
            try {
                token = await user.getIdToken();
            } catch (error) {
                console.error('[Firebase] Failed to read refreshed ID token:', error);
            }
        }
        idTokenSubscribers.forEach((subscriber) => {
            try {
                subscriber(token, user);
            } catch (error) {
                console.error('[Firebase] ID token subscriber failed:', error);
            }
        });
    }

    /** Called with every ID token Firebase mints, refreshes included. */
    function subscribeIdToken(callback) {
        if (typeof callback !== 'function') return () => {};
        idTokenSubscribers.add(callback);
        return () => idTokenSubscribers.delete(callback);
    }

    /**
     * Get ID token for Convex
     */
    async function getIdToken() {
        if (!currentUser) {
            return null;
        }

        try {
            const token = await currentUser.getIdToken();
            return token;
        } catch (error) {
            console.error('[Firebase] Failed to get ID token:', error);
            return null;
        }
    }

    /**
     * Sign in with Google
     */
    async function signInWithGoogle() {
        if (!firebaseAuth) {
            throw new Error('Firebase not initialized');
        }

        const provider = new firebase.auth.GoogleAuthProvider();
        // Always show the Google account chooser instead of silently reusing
        // the last session — otherwise logging out and back in skips the picker.
        provider.setCustomParameters({ prompt: 'select_account' });

        try {
            const result = await firebaseAuth.signInWithPopup(provider);
            console.log('[Firebase] Google sign-in successful');
            return result.user;
        } catch (error) {
            console.error('[Firebase] Google sign-in failed:', error);
            throw error;
        }
    }

    /**
     * Sign in with email/password
     */
    async function signInWithEmail(email, password) {
        if (!firebaseAuth) {
            throw new Error('Firebase not initialized');
        }

        try {
            const result = await firebaseAuth.signInWithEmailAndPassword(email, password);
            console.log('[Firebase] Email sign-in successful');
            return result.user;
        } catch (error) {
            console.error('[Firebase] Email sign-in failed:', error);
            throw error;
        }
    }

    /**
     * Sign up with email/password
     */
    async function signUpWithEmail(email, password) {
        if (!firebaseAuth) {
            throw new Error('Firebase not initialized');
        }

        try {
            const result = await firebaseAuth.createUserWithEmailAndPassword(email, password);
            console.log('[Firebase] Email sign-up successful');
            // A NEW Moonfall account needs a verified email (owner decision,
            // 2026-09-26), so the link goes out with the sign-up itself. Not
            // awaited as a condition: the verification screen can send it again.
            await sendVerificationEmail(result.user);
            return result.user;
        } catch (error) {
            console.error('[Firebase] Email sign-up failed:', error);
            throw error;
        }
    }

    /**
     * Mail the address-verification link to an email/password account. The
     * link brings the player back to /play. A continue URL Firebase does not
     * accept (a domain outside the project's authorized list) falls back to
     * Firebase's own landing page rather than to no email at all.
     *
     * @returns {Promise<{ok: boolean, code?: string}>}
     */
    async function sendVerificationEmail(user = currentUser) {
        const target = user || firebaseAuth?.currentUser;
        if (!target) return { ok: false, code: 'no-current-user' };
        try {
            await target.sendEmailVerification({ url: `${window.location.origin}/play` });
            return { ok: true };
        } catch (error) {
            const code = String(error?.code || error?.message || 'unknown');
            if (code === 'auth/unauthorized-continue-uri' || code === 'auth/invalid-continue-uri') {
                try {
                    await target.sendEmailVerification();
                    return { ok: true };
                } catch (retryError) {
                    return { ok: false, code: String(retryError?.code || retryError?.message || 'unknown') };
                }
            }
            console.warn('[Firebase] Verification email failed:', code);
            return { ok: false, code };
        }
    }

    /**
     * Re-read the account from Firebase. A verification completed in the mail
     * app (another tab, another device) reaches this page only after a reload,
     * and reaches Convex only in a NEWLY minted ID token, so a verified account
     * is also given a fresh token here.
     *
     * @returns {Promise<{verified: boolean}>}
     */
    async function refreshEmailVerification() {
        const user = firebaseAuth?.currentUser || currentUser;
        if (!user) return { verified: false };
        await user.reload();
        const fresh = firebaseAuth?.currentUser || user;
        if (!fresh.emailVerified) return { verified: false };
        await fresh.getIdToken(true);
        return { verified: true };
    }

    /**
     * Sign out
     */
    /**
     * Delete the signed-in Firebase identity.
     *
     * Account deletion used to erase every Convex row and then merely SIGN OUT,
     * leaving the authentication record — the email address and auth identifier
     * the Privacy Policy lists as account data — alive in Firebase forever. A
     * player who deleted their account and came back was recognised as the same
     * identity. (It is also why a returning player kept being offered the same
     * suggested username: that suggestion is seeded on the surviving uid.)
     *
     * Firebase refuses this without a recent sign-in, and that refusal must be
     * reported rather than swallowed: the caller has already erased the data, so
     * pretending the identity is gone would be a false claim about an erasure.
     *
     * @returns {Promise<{ok: boolean, code?: string}>}
     */
    async function deleteCurrentUser() {
        if (!firebaseAuth || !currentUser) return { ok: false, code: 'no-current-user' };
        try {
            await currentUser.delete();
            console.log('[UI.Firebase] Identity deleted');
            return { ok: true };
        } catch (error) {
            const code = String(error?.code || error?.message || 'unknown');
            console.warn('[UI.Firebase] Identity deletion failed:', code);
            return { ok: false, code };
        }
    }

    async function signOut() {
        if (!firebaseAuth) {
            return;
        }

        try {
            await firebaseAuth.signOut();
            console.log('[Firebase] Signed out');
        } catch (error) {
            console.error('[Firebase] Sign out failed:', error);
        }
    }

    // Public API
    return {
        init,
        subscribe,
        subscribeIdToken,
        isSignedIn,
        getUser,
        getIdToken,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        sendVerificationEmail,
        refreshEmailVerification,
        signOut,
        deleteCurrentUser,
    };
})();

export { Firebase };
